// Mirror of Shared/Calculators/WallpaperCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 1, max: 200 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 1, max: 200 },
  { key: "ceilingHeightFt", kind: "number", default: 8, min: 6, max: 20 },
  { key: "doors", kind: "number", default: 1, min: 0, max: 20 },
  { key: "windows", kind: "number", default: 2, min: 0, max: 30 },
  {
    key: "rollType",
    kind: "select",
    defaultChoice: "double",
    choices: [
      { value: "double", set: { rollCoverageSqft: 56 } },
      { value: "euro", set: { rollCoverageSqft: 56 } },
      { value: "single", set: { rollCoverageSqft: 28 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 15, min: 0, max: 40 },
  { key: "pricePerRoll", kind: "number", default: 30, min: 0, max: 300 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const h = F.get(i, "ceilingHeightFt");
  const doors = F.get(i, "doors");
  const windows = F.get(i, "windows");
  const rollCov = F.get(i, "rollCoverageSqft");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerRoll");

  const wallArea = 2 * (w + l) * h;
  const deductions = doors * 21 + windows * 15;
  const paintable = Math.max(0, wallArea - deductions);
  const withWaste = paintable * (1 + waste / 100);
  const rolls = rollCov > 0 ? F.ceilUnits(withWaste / rollCov) : 0;
  const cost = rolls * price;

  return {
    wallArea: F.round(wallArea, 2),
    paintable: F.round(paintable, 2),
    withWaste: F.round(withWaste, 2),
    rolls,
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const rolls = F.get(r, "rolls");
  return [
    { label: "Wall area", value: `${F.dec(F.get(r, "wallArea"))} sq ft` },
    { label: "Area to cover", value: `${F.dec(F.get(r, "paintable"))} sq ft` },
    { label: "Rolls to buy", value: `${F.intStr(rolls)} ${F.plural(rolls, "roll", "rolls")}`, highlight: true },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — wallpaper only, excludes paste, tools & labor" },
  ];
}

export const wallpaper: CalcDef = { slug: "wallpaper-calculator", fields, compute, present };
